import React from "react";
import PropTypes from "prop-types";
import Heading from "@react/react-spectrum/Heading";
import { I18n } from "react-redux-i18n";
import ReactHtmlParser from "react-html-parser";

import { isHttp400SeriesClientError } from "./httpErrorPriority";
import unauthorisedImg from "./error_images/unauthorised.png";
import unableToLoadImg from "./error_images/unable_to_load.png";
import "./AppError.scss";

const DEFAULT_TITLE = "Something went wrong";
const DEFAULT_MESSAGE = "An unexpected error occurred. Please try again later.";
const GENERIC_MESSAGE = "Something went wrong, please try after sometime.";

// Backend messages may contain simple markup (e.g. "<b>Primary Contact</b>");
// render that markup instead of showing the raw tags as text.
const renderMessage = (msg) =>
  typeof msg === "string" ? ReactHtmlParser(msg) : msg;

/**
 * Full-page error presentation for blocking failures (e.g. initial load failed).
 * Supports multiple messages; no dismiss/close chrome — informational only.
 */
function AppErrorPage({ title, messages, errorPayload, children }) {
  const rawList = Array.isArray(messages)
    ? messages
    : messages != null
      ? [messages]
      : [];
  const normalized = rawList
    .map((m) => (typeof m === "string" ? m.trim() : m))
    .filter((m) => m !== "" && m != null);

  const items =
    normalized.length > 0 ? normalized : children ? [] : [DEFAULT_MESSAGE];

  const is4xx =
    errorPayload != null &&
    typeof errorPayload === "object" &&
    isHttp400SeriesClientError(errorPayload);

  const resolvedTitle = is4xx
    ? I18n.t("global.errors.unauthorized")
    : I18n.t("global.errors.unable_to_load");
  const illustrationSrc = is4xx ? unauthorisedImg : unableToLoadImg;

  return (
    <div className="app-error-page" role="alert">
      <div className="app-error-page__inner page-container">
        <div className="app-error-page__illustration-wrap" aria-hidden>
          <img
            src={illustrationSrc}
            alt=""
            className="app-error-page__illustration"
            style={{ width: "150px", height: "100px" }}
          />
        </div>
        <div className="app-error-page__title-wrap">
          <Heading
            variant="pageTitle"
            style={{
              fontWeight: "600",
              fontSize: "22px",
              color: "#6c6c6c",
            }}
          >
            {resolvedTitle}
          </Heading>
        </div>
        {items.length === 1 ? (
          <div className="app-error-page__detail-panel">
            <div
              className="app-error-page__item"
              style={{ margin: 0, letterSpacing: "0.4px" }}
            >
              {renderMessage(items[0])}
            </div>
          </div>
        ) : items.length > 1 ? (
          <div className="app-error-page__detail-panel">
            <ul className="app-error-page__list">
              {items.map((msg, i) => (
                <li
                  key={i}
                  className="app-error-page__item"
                  style={{ letterSpacing: "0.4px" }}
                >
                  {renderMessage(msg)}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="app-error-page__detail-panel">
            <div
              className="app-error-page__item"
              style={{ letterSpacing: "0.4px" }}
            >
              {GENERIC_MESSAGE}
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

AppErrorPage.propTypes = {
  /** Kept for API compatibility; headline uses `global.errors.*` from i18n. */
  title: PropTypes.string,
  /** When set, HTTP 4xx (400–499, including 422) uses unauthorized image + `global.errors.unauthorized`. */
  errorPayload: PropTypes.object,
  messages: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node])),
    PropTypes.string,
    PropTypes.node,
  ]),
  children: PropTypes.node,
};

AppErrorPage.defaultProps = {
  title: DEFAULT_TITLE,
  errorPayload: null,
  messages: null,
  children: null,
};

export default AppErrorPage;
